import { ActionIcon, Box, Button, Group, Text, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { Split } from 'components/index';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isNullish } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import { useDeleteComment } from 'shared/hooks/use-delete-comment';
import type { Comment } from 'shared/hooks/use-load-comments';
import { useUpdateComment } from 'shared/hooks/use-update-comment';

type CommentProps = {
  comment: Comment;
  isBeingEdited: boolean;
  isEditingDisabled: boolean;
  setIsBeingEdited: (val: boolean) => void;
};

export const CommentItem = ({
  comment,
  isBeingEdited,
  setIsBeingEdited,
  isEditingDisabled,
}: CommentProps) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const { deleteComment, isDeleteCommentLoading } = useDeleteComment();
  const { updateComment, isUpdateCommentLoading } = useUpdateComment();

  const [editedMessage, setEditedMessage] = useState('');

  const { id, author, comment: message } = comment;

  const isAuthor = profile?.id === author?.id;
  const isLoading = isDeleteCommentLoading || isUpdateCommentLoading;

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t(i18nKeys.COMMENTS_ATTRIBUTES.DELETE),
      centered: true,
      children: <Text size="sm">{t(i18nKeys.COMMENTS_ATTRIBUTES.DELETE_CONFIRMATION)}</Text>,
      labels: { confirm: t(i18nKeys.DELETE), cancel: t(i18nKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteComment({ id: Number(id) }),
    });
  };

  const handleEdit = () => {
    setEditedMessage(message);
    setIsBeingEdited(true);
  };

  const handleSave = () => {
    if (editedMessage.trim() && editedMessage !== message) {
      updateComment({
        id: Number(id),
        comment: editedMessage,
      });
    }
    setIsBeingEdited(false);
  };

  const handleCancel = () => {
    setIsBeingEdited(false);
    setEditedMessage('');
  };

  return (
    <Box>
      {isBeingEdited ? (
        <Box>
          <Textarea
            radius={8}
            value={editedMessage}
            onChange={e => setEditedMessage(e.target.value)}
            minRows={3}
            mb="xs"
          />
          <Group>
            <Button
              size="xs"
              onClick={handleSave}
              disabled={isLoading || !editedMessage.trim() || editedMessage === message}
            >
              {t(i18nKeys.COMMENTS_ATTRIBUTES.SAVE)}
            </Button>
            <Button size="xs" variant="outline" onClick={handleCancel} disabled={isLoading}>
              {t(i18nKeys.COMMENTS_ATTRIBUTES.CANCEL)}
            </Button>
          </Group>
        </Box>
      ) : (
        <Box
          p="md"
          style={{
            borderRadius: 8,
            border: '1px solid',
            borderColor: 'var(--mantine-color-gray-2)',
          }}
        >
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw="bold" c="gray.7">
              {author?.fullName ?? t(i18nKeys.RECOVR)}
            </Text>
            <Text size="xs" c="dimmed">
              {dayjs(comment.date).format('L • LT')}
            </Text>
          </Group>
          <Split left={90}>
            {isNullish(comment.author) ? (
              <HTML html={message} />
            ) : (
              <Text size="sm" c="gray.10">
                {message}
              </Text>
            )}
            <Group gap={0} justify="end">
              {isAuthor && (
                <ActionIcon
                  onClick={handleEdit}
                  disabled={isLoading || isEditingDisabled}
                  variant="subtle"
                  size="sm"
                >
                  <IconEdit size={16} />
                </ActionIcon>
              )}
              {(isAuthor || isNullish(author)) && (
                <ActionIcon
                  color="red"
                  onClick={handleDelete}
                  disabled={isLoading}
                  variant="subtle"
                  size="sm"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </Group>
          </Split>
        </Box>
      )}
    </Box>
  );
};
